import React, { useEffect, useState, useRef, useContext } from 'react';
import styles from './ApplicationsPage.module.scss';
import ResearchDetails from '../../Molecules/detailsBox/researchDetailsBox';
import TableTopHeader from '../../Molecules/TableTopHeader/ApplicationsTableTopHeader';
import ApplicantsTable from '../../Molecules/Applications/ApplicantsTable';
import {
  DetailsContext,
  PageTitleContext,
} from '../../HOC/GeneralBody/GeneralBody';
import {
  useFetchResearch,
  useExportTableResearch,
  useExportSingleResearch,
  useSetResearchStatus,
  useFetchSingleResearch,
  useFetchResearchDocuments,
} from '../../../Hooks/Applications';
import ResearchFilters from '../../Molecules/Applications/ResearchFilters';
import {
  EMPTY_RESEARCH_FILTERS,
  RESEARCH_STATUS,
} from '../../../app/helpers/researchQuery';
import { saveBlob, exportFileName } from '../../../app/helpers/saveBlob';
import { useToast } from '../../Molecules/Custom/CustomToaster/Toast';
import useDebouncedValue from '../../../Hooks/useDebouncedValue';

const ApplicationsPage = () => {
  const [Page, setPage] = useState(1);
  const [PageSize, setPageSize] = useState(10);
  const HeaderAttr = useContext(PageTitleContext);
  const Details = useContext(DetailsContext);

  const [selected, setSelected] = useState('research');
  const [applicationId, setApplicationId] = useState('');

  /**
   * TWO SEARCH VALUES, ON PURPOSE.
   *
   * `searchText` is what is in the box, and it updates on every keystroke so
   * typing stays instant. `search` is what the API is asked about, and it only
   * catches up once typing has been quiet.
   *
   * Before this there was one value, so an Arabic name sent one request per
   * letter — each one already stale before it landed, and all of them racing
   * to decide what the table showed.
   *
   * 350ms rather than the year boxes' 450: a search term is read as you type
   * it, so the feedback wants to be quicker, and a partial term still returns
   * meaningful rows where a partial year returns HTTP 400.
   */
  const [searchText, setSearchText] = useState('');
  const search = useDebouncedValue(searchText, 350);

  const [order, setOrder] = useState(['', 'asc']);
  const [mode, setMode] = useState('research');

  /**
   * Everything the operator has narrowed the list by, in the exact parameter
   * names the endpoint binds. Keeping the API's own spelling here means the
   * query builder is a pass-through and there is no translation layer to get
   * out of step with Swagger.
   */
  const [filters, setFilters] = useState(EMPTY_RESEARCH_FILTERS);

  /** Search and sort live in the same query string as the rest. */
  const queryFilters = {
    ...filters,
    SearchText: search || '',
    SortBy: order[0] || '',
    SortDirection: order[0] ? order[1] : '',
  };

  const {
    data: research,
    refetch: refetchResearch,
    isLoading: isLoading,
  } = useFetchResearch({
    pageSize: PageSize,
    pageNumber: Page,
    filters: queryFilters,
  });
  const {
    data: researchPrint,
    refetch: refetchResearchPrint,
    isLoading: isLoading000000,
  } = useFetchResearch({
    pageSize: 10000,
    pageNumber: 1,
    filters: queryFilters,
  });
  const {
    data: ResearchbyId,
    refetch: refetchSingleResearch,
    isLoading: isLoadingResearchById,
  } = useFetchSingleResearch(Details.details);

  const {
    data: researchDetails,
    refetch: refetchDetails,
    isLoading: isLoadingDetails,
  } = useFetchResearchDocuments(Details.details);

  const toast = useToast();
  const exportResearchTable = useExportTableResearch();
  const exportOneResearch = useExportSingleResearch();
  const setStatus = useSetResearchStatus();

  /** Ids the operator has ticked. Cleared whenever the result set changes. */
  const [selectedIds, setSelectedIds] = useState([]);
  /** §A4 — whether an export brings the attachments with it. */
  const [includeAttachments, setIncludeAttachments] = useState(false);

  /**
   * Export: the whole filtered table, or only what is ticked.
   *
   * The old handler tested `data instanceof Blob`, which is true of an ERROR
   * body too — `responseType: 'blob'` wraps a 401 or a 500 in exactly the same
   * type as a spreadsheet. So a failure downloaded as `ResearchesTable` and
   * the operator got a file Excel refused to open, with a console.error
   * nobody reads as the only clue. `saveBlob` checks the media type and
   * surfaces the reason instead.
   */
  const handleExportTable = async () => {
    try {
      const res = await exportResearchTable.mutateAsync({
        // The export carries the same filters as the view, unless rows are
        // ticked — then the selection IS the request.
        filters: queryFilters,
        ids: selectedIds,
        includeAttachments,
      });
      const saved = await saveBlob({
        data: res.data,
        fileName: exportFileName('الأبحاث', { zip: includeAttachments }),
        onError: (msg) => toast.open(msg),
      });
      if (saved) toast.open('تم تنزيل الملف');
    } catch (error) {
      toast.open('تعذر تنزيل الملف');
    }
  };

  /** §A2 — one record, from its row. */
  const handleExportOne = async (id) => {
    try {
      const res = await exportOneResearch.mutateAsync({
        id,
        includeAttachments,
      });
      const saved = await saveBlob({
        data: res.data,
        fileName: exportFileName(`بحث-${String(id).slice(0, 8)}`, {
          zip: includeAttachments,
        }),
        onError: (msg) => toast.open(msg),
      });
      if (saved) toast.open('تم تنزيل الملف');
    } catch (error) {
      toast.open('تعذر تنزيل الملف');
    }
  };

  /**
   * §A1 — the operator setting a status by hand.
   *
   * Both views are refetched: the detail so the control reflects what was
   * saved rather than what was clicked, and the list so the badge and any
   * status filter behind this screen agree with it.
   */
  const handleStatusChange = async (status) => {
    try {
      await setStatus.mutateAsync({ id: Details.details, status });
      await Promise.all([refetchSingleResearch(), refetchResearch()]);
      toast.open('تم تحديث الحالة');
    } catch (error) {
      toast.open('تعذر تحديث الحالة');
    }
  };

  const toggleSelect = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  /** All-or-nothing for the CURRENT page; see the note in ApplicantsTable. */
  const toggleSelectAll = (pageIds) =>
    setSelectedIds((prev) =>
      pageIds.every((id) => prev.includes(id))
        ? prev.filter((id) => !pageIds.includes(id))
        : [...new Set([...prev, ...pageIds])]
    );
  /**
   * Registry columns, October 2026.
   *
   * `originalName` is what the sort sends to the API as `OrderBy`, so each
   * entry must name a REAL field on the record. `fullName` replaced
   * `firstName`/`lastName`; `submissionKind`, the entry title and the
   * publication year are new. `researchTypeDisplay` (مجال البحث) moved to the
   * detail screen — seven columns is already the practical limit here, and an
   * operator triages on what kind of entry it is, not on its sector.
   */
  const researchTableHeader = [
    {
      name: 'الاسم',
      originalName: 'fullName',
    },
    {
      name: 'الحالة',
      originalName: 'status',
    },
    {
      name: 'نوع التسجيل',
      originalName: 'submissionKind',
    },
    {
      name: 'العنوان',
      // Shipped as ONE `title` for both branches, not the `researchTitle` /
      // `studyTitle` pair the specification asked for.
      originalName: 'title',
    },
    {
      name: 'سنة النشر',
      originalName: 'publicationYear',
    },
    {
      name: 'البريد الإلكتروني',
      originalName: 'email',
    },
    {
      name: 'التاريخ',
      // Likewise `date`: the registry sends `submittedOn`.
      originalName: 'submittedOn',
    },
  ];

  /**
   * Opening a NEW submission marks it read (§A1).
   *
   * This is what makes the status worth having: "new" has to mean "nobody has
   * looked at this", and that cannot be maintained by hand. Only `new` is
   * touched — an archived record stays archived when someone reads it again,
   * and a failure is deliberately silent, because the operator came here to
   * read a submission, not to manage its status.
   */
  useEffect(() => {
    if (!Details.details) return;
    const entry = ResearchbyId?.data?.[0];
    if (entry && String(entry.status).toLowerCase() === RESEARCH_STATUS.NEW) {
      setStatus
        .mutateAsync({ id: Details.details, status: RESEARCH_STATUS.READ })
        .then(() => refetchResearch())
        .catch(() => {});
    }
  }, [Details.details, ResearchbyId]);

  useEffect(() => {
    if (Details.details) {
      HeaderAttr.setMoveRight('40px');

      refetchSingleResearch();
      refetchDetails();
      HeaderAttr.setPathName('تفاصيل البحث');
    } else {
      HeaderAttr.setMoveRight('2px');
      HeaderAttr.setPathName('الأبحاث العلمية');
      Details.setDetails(null);
    }
  }, [Details.details]);

  /**
   * A NEW SEARCH TERM STARTS AT PAGE 1.
   *
   * Page 4 of the unfiltered list is usually past the end of a searched one,
   * and an empty table reads as "no results" rather than "wrong page" — the
   * same reasoning as `handleFiltersChange`.
   *
   * THE MANUAL REFETCH THAT USED TO LIVE HERE IS GONE. It ran on every change
   * to Page, PageSize, search, order or filters and called `refetchResearch()`
   * by hand, which was necessary only because `useFetchResearch` keyed on the
   * constant string "research" and therefore never refetched on its own. The
   * key now contains the page coordinates and the query string, so react-query
   * refetches when the request actually changes — and the hand-rolled call had
   * become a second, duplicate request for every one of them.
   */
  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [search]);

  /**
   * The count the filter bar shows.
   *
   * Read live from the response rather than latched on first load. It was
   * `if (research && !researchNumber)`, which captured the very first total and
   * then never updated — so the moment a filter narrowed the list the number
   * beside it would have been the unfiltered one, which is precisely the
   * opposite of what the review asked for ("the count updating appropriately
   * when filters are applied").
   */
  const totalCount = research?.data?.totalCount ?? 0;

  const handleFiltersChange = (next) => {
    setFilters(next);
    // A selection is of ROWS the operator could see. Once the filter changes,
    // some of them may no longer be in the result set, and exporting ids the
    // screen no longer shows is the sort of thing nobody notices until the
    // wrong records have been sent on.
    setSelectedIds([]);
    // Any change to the filters invalidates the page the operator is on: page
    // 4 of an unfiltered list is usually past the end of a filtered one, and
    // an empty table reads as "no results" rather than "wrong page".
    setPage(1);
  };

  const handleFiltersReset = () => {
    setFilters(EMPTY_RESEARCH_FILTERS);
    setSelectedIds([]);
    setPage(1);
  };

  const [moveRight, setMoveRight] = useState(false);

  useEffect(() => {
    setSearchText('');
    setPage(1);
    setPageSize(10);
    setOrder(['', 'asc']);
  }, [selected]);

  // useEffect(() => {
  //   refetchSingleResearch();
  // }, []);

  return (
    <>
      {!Details.details ? (
        <div className={styles['container']}>
          <div className={styles['search-container']}>
            <div className={styles['table-container']}>
              <TableTopHeader
                mode={mode}
                handleExport={handleExportTable}
                search={searchText}
                setSearch={setSearchText}
                /* The heading on the printed sheet, and the filename the
                   browser offers when the operator picks "Save as PDF". It was
                   "Research Table" — English, on an Arabic right-to-left
                   document the Academy circulates internally. */
                title="الأبحاث العلمية"
                data={researchPrint?.data}
                refetch={refetchResearchPrint}
                tableHeader={researchTableHeader}
                hideExport
              />
              {/* October review: filters beyond search, and a count that
                  responds to them. Every control maps to a parameter the
                  endpoint actually binds — see ResearchFilters. */}
              <ResearchFilters
                filters={filters}
                onChange={handleFiltersChange}
                onReset={handleFiltersReset}
                total={totalCount}
                isLoading={isLoading}
                selectedCount={selectedIds.length}
                onClearSelection={() => setSelectedIds([])}
                includeAttachments={includeAttachments}
                onToggleAttachments={setIncludeAttachments}
                onExport={handleExportTable}
              />
              <ApplicantsTable
                data={research?.data}
                tableHeader={researchTableHeader}
                mode={mode}
                page={Page}
                setPage={setPage}
                pageSize={PageSize}
                setPageSize={setPageSize}
                isApplicantsLoading={isLoading}
                setDetails={Details.setDetails}
                details={Details.details}
                setApplicationId={setApplicationId}
                order={order}
                setOrder={setOrder}
                refetch={refetchResearch}
                setMoveRight={setMoveRight}
                isLoading={isLoading}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                onExportOne={handleExportOne}
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          <ResearchDetails
            data={ResearchbyId?.data}
            onStatusChange={handleStatusChange}
            onAttachmentError={(msg) => toast.open(msg)}
            setDetails={Details.setDetails}
            details={Details.details}
            researchId={Details.details}
            refetchSingleResearch={refetchSingleResearch}
            setMoveRight={setMoveRight}
            moveRight={moveRight}
            isLoading={isLoadingResearchById}
            researchDetails={researchDetails?.data}
            isLoadingDetails={isLoadingDetails}
          />
        </>
      )}
    </>
  );
};

export default ApplicationsPage;
